import { SectionHeader } from '../common/section-header'
import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import { queryKeys } from '../api/query-keys'
import { DateFilterInput } from '../common/date-filter-input'
import { FilterField, ListPage, ListPagination } from '../common/list-page'
import { TABLE, TABLE_HEAD_ROW, TABLE_ROW, TABLE_WRAP } from '../common/table'
import { Button, Notice, NoticeAction, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@speaking-coach/ui'
import { BackToDetailsLink } from './history-link'
import { activityApi, messageForActivityError } from './api'
import { formatActivityTimestamp, jstDayStart } from './activity-presentation'
import {
  contentHistoryDetailReturn,
  defaultHistoryDays,
  historyIdentity,
  historyReason,
  historyRows,
  historyWindow,
} from './content-history-model'
import {
  CONTENT_KINDS,
  type ContentKind,
  type ContentHistory,
} from './content-history-types'
import { AvailabilityBadge, ContentStatusBadge } from './content-history-badges'
import { HistoryTargetPicker, useHistoryTarget } from './history-target-picker'
import { OperatorFaqTrigger } from '../operator-faq/operator-faq'

const PAGE_SIZE = 50
export function ContentHistoryPage() {
  const [search, setSearch] = useSearchParams()
  const detailReturn = contentHistoryDetailReturn(search)
  const [defaults] = useState(defaultHistoryDays)
  const [page, setPage] = useState(0)
  const rawKind = search.get('targetType') ?? 'scenario'
  const kind: ContentKind = Object.hasOwn(CONTENT_KINDS, rawKind)
    ? (rawKind as ContentKind)
    : 'scenario'
  const targetId = search.get('targetId') ?? ''
  const programId = search.get('programId') ?? ''
  const from = search.get('from') ?? defaults.from,
    to = search.get('to') ?? defaults.to
  const cefrLevel = search.get('cefrLevel') ?? ''
  const validTargetId = historyIdentity.safeParse(targetId).success
  const selected = useHistoryTarget(kind, targetId)
  const target = selected.data?.items[0]
  const tutor = kind === 'tutor' || kind === 'tutor_prompt'
  const needsProgram = tutor || target?.requiresProgram === true
  const window = historyWindow.safeParse({ from, to })
  const validProgram = !needsProgram || historyIdentity.safeParse(programId).success
  const validLevel = !tutor || !cefrLevel || selected.data?.cefrLevels.some(level => level === cefrLevel) === true
  const params = {
    targetType: kind,
    targetId,
    from: jstDayStart(from),
    to: jstDayStart(to),
    ...(needsProgram && programId ? { programId } : {}),
    ...(tutor && cefrLevel ? { cefrLevel } : {}),
  }
  const ready =
    !!target &&
    window.success &&
    validProgram && validLevel && Object.hasOwn(CONTENT_KINDS, rawKind)
  const result = useQuery({
    queryKey: queryKeys.contentHistory(params),
    queryFn: () => activityApi.contentHistory(params),
    enabled: ready,
    refetchOnWindowFocus: false,
  })
  const rows = useMemo(
    () => (result.data ? historyRows(result.data) : []),
    [result.data],
  )
  const useEvidenceReasons = result.data?.useEvidence.reasons ?? []
  const catalogEvidenceReasons = result.data?.coverage.reasons.filter(
    reason => !useEvidenceReasons.includes(reason),
  ) ?? []
  const lastPage = Math.max(0, Math.ceil(rows.length / PAGE_SIZE) - 1)
  const currentPage = Math.min(page, lastPage)
  function update(values: Record<string, string | null>) {
    const next = new URLSearchParams(search)
    next.set('from', from)
    next.set('to', to)
    for (const [key, value] of Object.entries(values)) {
      if (value !== null) next.set(key, value)
      else next.delete(key)
    }
    setPage(0)
    setSearch(next)
  }
  const filters = (
    <>
      <FilterField label="Content type" htmlFor="history-kind">
        <Select
          value={kind}
          onValueChange={(value) =>
            update({
              targetType: value,
              targetId: null,
              programId: null,
              cefrLevel: null,
            })
          }
        >
          <SelectTrigger id="history-kind" size="sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(CONTENT_KINDS).map(([key, label]) => (
              <SelectItem key={key} value={key}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FilterField>
      <FilterField
        label="Content"
        htmlFor="history-target"
        help="Search current names and stable content keys, including retired content. Picker status is current; the table below uses historical evidence."
      >
        <HistoryTargetPicker
          key={kind}
          kind={kind}
          id="history-target"
          label="Content"
          value={targetId}
          onChange={(value) => update({ targetId: value, programId: null })}
        />
      </FilterField>
      {needsProgram && (
        <FilterField label="Program context" htmlFor="history-program">
          <HistoryTargetPicker
            kind="program"
            id="history-program"
            label="Program context"
            value={programId}
            onChange={(value) => update({ programId: value })}
          />
        </FilterField>
      )}
      {tutor && selected.data && (
        <FilterField
          label="Learner level"
          htmlFor="history-cefr"
          help="Used to assess tutor eligibility. Call counts cover all levels in the selected Program."
        >
          <Select
            value={cefrLevel || 'unplaced'}
            onValueChange={(value) =>
              update({ cefrLevel: value === 'unplaced' ? null : value })
            }
          >
            <SelectTrigger id="history-cefr" size="sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="unplaced">Not yet placed</SelectItem>
              {selected.data.cefrLevels.map((level) => (
                <SelectItem key={level} value={level}>
                  {level}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FilterField>
      )}
      <FilterField label="From (JST)" htmlFor="history-from">
        <DateFilterInput
          id="history-from"
          label="From"
          value={from}
          onValueChange={(value) => update({ from: value })}
          size="sm"
        />
      </FilterField>
      <FilterField
        label="Until (JST, exclusive)"
        htmlFor="history-to"
        help="The end day is excluded. For one day, choose that day as From and the following day as Until. Maximum 31 days."
      >
        <DateFilterInput
          id="history-to"
          label="Until"
          value={to}
          onValueChange={(value) => update({ to: value })}
          size="sm"
        />
      </FilterField>
    </>
  )
  return (
    <ListPage
      title="Content history"
      search={
        <p className="truncate text-body">
          {target?.label ?? 'Select content to inspect its history'}
        </p>
      }
      filters={filters}
      activeFilterCount={
        2 + Number(!!targetId) + Number(!!programId) + Number(!!cefrLevel)
      }
      action={<div className="flex items-center gap-2">{detailReturn ? <BackToDetailsLink to={detailReturn} /> : null}
        <OperatorFaqTrigger
          topicId="content-history-meaning"
          label="How to read this report"
          size="sm"
        />
        {ready ? (
          <Button
            variant="secondary"
            size="sm"
            disabled={result.isFetching}
            onClick={() => void result.refetch()}
          >
            Refresh history
          </Button>
        ) : null}</div>
      }
      pagination={
        ready && result.data && rows.length > PAGE_SIZE ? (
          <ListPagination
            summary={`${rows.length} periods · times in JST`}
            page={currentPage}
            pageCount={lastPage + 1}
            previousDisabled={currentPage === 0}
            nextDisabled={currentPage === lastPage}
            onPrevious={() => setPage(currentPage - 1)}
            onNext={() => setPage(currentPage + 1)}
          />
        ) : undefined
      }
    >
      <div className="space-y-5">
        {!window.success && (
          <Notice variant="danger">
            Choose an end after the start, within 31 days.
          </Notice>
        )}
        {!targetId && (
          <Notice variant="neutral">
            Choose a content type and search for content to view its history.
          </Notice>
        )}
        {targetId && !validTargetId && <Notice variant="danger">Invalid content identifier. Select content using the picker.</Notice>}
        {!Object.hasOwn(CONTENT_KINDS, rawKind) && <Notice variant="danger">Unknown content type. Choose a type from the filter.</Notice>}
        {tutor && cefrLevel && selected.isSuccess && !validLevel && <Notice variant="danger">Unknown learner level. Choose a supported level.</Notice>}
        {needsProgram && programId && !validProgram && <Notice variant="danger">Invalid Program context. Select a Program using the picker.</Notice>}
        {targetId && validTargetId && selected.isPending && (
          <p role="status">Loading content…</p>
        )}
        {targetId && (selected.isError || (selected.isSuccess && !target)) && (
          <Notice variant="danger" action={<NoticeAction onClick={() => void selected.refetch()}>Retry lookup</NoticeAction>}>
            Could not resolve this content. Choose another item or retry.
          </Notice>
        )}
        {needsProgram && !programId && (
          <Notice variant="neutral">
            Select a Program context to inspect this content.
          </Notice>
        )}
        {ready && result.isPending && (
          <p role="status">Loading content history…</p>
        )}
        {ready && result.isError && (
          <Notice variant="danger" action={<NoticeAction onClick={() => void result.refetch()}>Try again</NoticeAction>}>
            {messageForActivityError(result.error)}
          </Notice>
        )}
        {ready && result.data && (
          <>
            <section
              aria-label="History scope"
              className="rounded-token-card border border-border-default bg-surface-card p-4 text-body"
            >
              <dl className="grid gap-3 sm:grid-cols-2">
                <div>
                  <dt className="text-text-secondary">
                    Evidence checked through (JST)
                  </dt>
                  <dd>
                    {formatActivityTimestamp(
                      result.data.coverage.observedUntil,
                    )}
                  </dd>
                </div>
                <div>
                  <dt className="text-text-secondary">
                    Window (JST, end excluded)
                  </dt>
                  <dd>
                    {formatActivityTimestamp(result.data.coverage.start)} –{' '}
                    {formatActivityTimestamp(result.data.coverage.end)}
                  </dd>
                </div>
                <div>
                  <dt className="text-text-secondary">
                    Recorded history starts (JST)
                  </dt>
                  <dd>
                    {result.data.coverage.baselineAt
                      ? formatActivityTimestamp(result.data.coverage.baselineAt)
                      : 'No baseline recorded'}
                  </dd>
                </div>
              </dl>
              {catalogEvidenceReasons.length > 0 && (
                <div className="mt-3 text-text-secondary">
                  <p className="font-medium text-text-primary">Evidence notes</p>
                  <ul className="mt-1 list-disc space-y-1 pl-5">
                    {[...new Set(catalogEvidenceReasons)].map(reason => <li key={reason}>{historyReason(reason)}</li>)}
                  </ul>
                  <div className="mt-2"><OperatorFaqTrigger topicId="content-history-evidence" label="How should I handle these notes?" /></div>
                </div>
              )}
            </section>
            <CallUse data={result.data} />
            <section aria-labelledby="availability-heading">
              <SectionHeader id="availability-heading" title="Status and availability" className="mb-3" />
              <div className={TABLE_WRAP}>
                <table className={TABLE}>
                  <thead>
                    <tr className={TABLE_HEAD_ROW}>
                      <th className="p-3">From (JST)</th>
                      <th className="p-3">Until (exclusive)</th>
                      <th className="p-3">Content status</th>
                      <th className="p-3">New-call availability</th>
                      <th className="p-3">
                        Blocking content or missing evidence
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows
                      .slice(
                        currentPage * PAGE_SIZE,
                        (currentPage + 1) * PAGE_SIZE,
                      )
                      .map((row) => (
                        <tr key={row.start} className={TABLE_ROW}>
                          <td className="p-3 whitespace-nowrap">
                            {formatActivityTimestamp(row.start)}
                          </td>
                          <td className="p-3 whitespace-nowrap">
                            {formatActivityTimestamp(row.end)}
                          </td>
                          <td className="p-3">
                            <ContentStatusBadge status={row.status} inheritedStatus={row.inheritedStatus} known={row.statusKnown} />
                          </td>
                          <td className="p-3">
                            <AvailabilityBadge available={row.available} />
                          </td>
                          <td className="p-3">
                            {row.blockers.length === 0 ? (
                              'None recorded'
                            ) : (
                              <ul className="space-y-1">
                                {row.blockers.map((blocker, index) => (
                                  <li key={index}>
                                    <Link
                                      className="underline"
                                      to={`/admin/content/history?${new URLSearchParams({ targetType: blocker.targetType, targetId: blocker.targetId, from, to, ...((blocker.targetType === 'tutor' || blocker.targetType === 'tutor_prompt') && programId ? { programId, ...(cefrLevel ? { cefrLevel } : {}) } : {}) })}`}
                                    >
                                      {CONTENT_KINDS[blocker.targetType]}:{' '}
                                      {historyReason(blocker.reason)}
                                    </Link>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        )}
      </div>
    </ListPage>
  )
}
function CallUse({ data }: { data: ContentHistory }) {
  const use = data.useEvidence
  return (
    <section
      aria-labelledby="use-heading"
      className="rounded-token-card border border-border-default bg-surface-card p-4 text-body"
    >
      <SectionHeader id="use-heading" title="Actual call use" />
      <p className="mt-2 text-product-title font-semibold">
        {use.retainedCallCount.toLocaleString()}{' '}
        <span className="text-body font-normal text-text-secondary">
          recorded started voice sessions
        </span>
      </p>
      {!use.complete ? (
        <div className="mt-3 text-text-secondary">
          <p className="font-medium text-text-primary">Call evidence notes</p>
          <ul className="mt-1 list-disc space-y-1 pl-5">
            {[...new Set(use.reasons)].map(reason => <li key={reason}>{historyReason(reason)}</li>)}
          </ul>
          <div className="mt-2"><OperatorFaqTrigger
              topicId="content-history-evidence"
              label="How should I handle these notes?"
            /></div>
        </div>
      ) : use.retainedCallCount === 0 ? (
        <p className="mt-2">No started calls recorded in this window.</p>
      ) : null}
      {use.firstUse && (
        <p className="mt-2">
          First recorded start: {formatActivityTimestamp(use.firstUse)} · Last:{' '}
          {formatActivityTimestamp(use.lastUse!)} (JST)
        </p>
      )}
      <p className="mt-2 text-text-secondary">
        {use.scope === 'target_and_program'
          ? 'For this content in the selected Program, across learner levels.'
          : 'For this content in the selected window.'}{' '}
        Setup-only attempts are excluded. Ongoing calls are counted by their
        voice-session start.
      </p>
    </section>
  )
}
